import { zodResolver } from '@hookform/resolvers/zod'
import { getRouteApi } from '@tanstack/react-router'
import { useForm, type DefaultValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Loading from '@/components/Loading'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import { toast } from '@/hooks/useToast'
import { useTimetableSetttings } from '@/modules/timetable'
import { parseNameAndIdToLabelAndValue } from '@/utils/parse-id-and-name-to-label-and-value'

import RoomForm from '../components/RoomForm'
import {
  roomFormPayload,
  type RoomFormPayload
} from '../constants/room-payload'
import useEditRoom from '../mutations/useEditRoom'
import { useRoom, type RoomDetails } from '../queries/useRoom'

const routeApi = getRouteApi('/_auth/rooms/$roomId/edit')

const RoomEditView = () => {
  const { roomId } = routeApi.useParams()

  const { t } = useTranslation(['rooms', 'common'])
  const { data: room, isLoading: isRoomLoading } = useRoom(roomId)

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.rooms'),
          to: '/rooms'
        },
        {
          title: t('header.edit-room'),
          to: '/rooms/$roomId/edit',
          params: {
            roomId
          }
        }
      ]}
      header={t('header.edit-room')}
      moduleName={t('common:header.administration')}
    >
      <Loading spinning={isRoomLoading}>
        {room ? <RoomEditForm room={room} /> : null}
      </Loading>
    </BasicLayout>
  )
}

const RoomEditForm = (props: { room: RoomDetails }) => {
  const navigate = routeApi.useNavigate()
  const { t } = useTranslation('rooms')

  const { data: workingHours, isLoading: isTimetableLoading } =
    useTimetableSetttings()

  const defaultValues: DefaultValues<RoomFormPayload> = {
    name: props.room.name,
    capacity: props.room.capacity,
    type: props.room.roomType,
    specialEquipment: props.room.specialEquipment ?? undefined,
    preferredCourses: props.room.preferredCourses.map(
      parseNameAndIdToLabelAndValue
    ),
    preferredUsers: props.room.preferredUsers.map(
      parseNameAndIdToLabelAndValue
    ),
    preferredClasses: props.room.preferredClasses.map(
      parseNameAndIdToLabelAndValue
    )
  }

  const form = useForm<RoomFormPayload>({
    defaultValues,
    resolver: workingHours
      ? zodResolver(roomFormPayload(workingHours))
      : undefined,
    mode: 'all'
  })

  const { mutate: editRoom, isPending: isEditRoomPending } = useEditRoom({
    id: props.room.id,
    onSuccess: ({ response }) => {
      toast({
        variant: 'success',
        title: t('toast.room-edit-success')
      })
      navigate({
        to: '/rooms/$roomId',
        params: {
          roomId: response.id
        }
      })
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.room-edit-error')
      })
    }
  })

  const isLoading = isTimetableLoading || isEditRoomPending

  return (
    <Loading spinning={isLoading}>
      <RoomForm
        form={form}
        onSubmit={editRoom}
        workingHours={workingHours}
        withoutAvailability
      />
    </Loading>
  )
}

export default RoomEditView
